/**
 * Main application component that handles routing, authentication, and layout structure.
 * Implements code splitting for better performance and manages user role-based navigation.
 * Update when adding new routes, modifying authentication flow, or changing layout structure.
 */
import React, { Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Route, Routes, useLocation } from 'react-router-dom';
import { Box, Container, Toolbar } from '@mui/material';

import {
  PARTNERSHIP_ROUTE_PATHS,
  PARTNERSHIP_SLUGS,
  partnershipRoutePath,
} from '@shared/partnerships/partnershipRoutes.js';
import { SUBJECT_PAGES } from '@shared/tuitionSubjects/subjectPages.js';

// Components
import ErrorBoundary from './components/common/ErrorBoundary';
import Header from './components/common/Header';
import {
  StudentLoadingCycleProvider,
  StudentLoadingCycleScope,
} from './components/common/StudentContentBoundary';
import {
  isBuilderCanvasRoute,
  NavigationDrawer,
  navWidthTransitionSx,
  useNavRail,
} from './components/navigation';
import AppRouteLoadingFallback from './components/routing/AppRouteLoadingFallback';
import AuthCallback from './components/routing/AuthCallback';
import EnhancedProtectedRoute from './components/routing/EnhancedProtectedRoute';
import LegacyRouteRedirect from './components/routing/LegacyRouteRedirect';
import LoginRedirector from './components/routing/LoginRedirector';
import ScrollToTop from './components/routing/ScrollToTop';
import { getBrandingConfig } from './config/runtimeBrandConfig';
import { BuilderCanvasActiveContext } from './contexts/builderCanvasContext';
import { FocusTransitionProvider } from './contexts/focusTransitionContext';
import { MathSubjectFocusProvider } from './contexts/mathSubjectFocusContext';
import { useAttributionTracking } from './hooks/useAttributionTracking';
import { useAuth } from './hooks/useAuth';
import { useDevRoutePersistence } from './hooks/useDevRoutePersistence';
import MaintenancePage from './pages/MaintenancePage';
import {
  marketingLazyPages,
  marketingSecondaryStreamRoutes,
} from './prerender/marketingRouteModules';
import { trackPageView } from './services/appMetrics';
import { useServerStatus } from './store/serverStatusStore';
import { normalizeRoutePath } from './utils/accessControl';
import { lazyWithChunkRetry } from './utils/lazyWithChunkRetry';

// Lazy-loaded Pages for code splitting (chunk load failures retry once, then reload page)
const TeacherWorksheetsPage = lazyWithChunkRetry(
  () => import('./pages/teacher/TeacherWorksheetsPage')
);
const WorksheetPrintView = lazyWithChunkRetry(
  () => import('./pages/teacher/worksheets/PrintView')
);
const CurriculumReview = lazyWithChunkRetry(
  () => import('./pages/CurriculumReview')
);
const DemoAnalytics = lazyWithChunkRetry(() => import('./pages/DemoAnalytics'));
const DemoPractice = lazyWithChunkRetry(() => import('./pages/DemoPractice'));
const AdaptivePracticePage = lazyWithChunkRetry(
  () => import('./pages/AdaptivePracticePage')
);
// Full-screen onboarding placement test — deliberately NOT in
// APP_SHELL_ROUTES so it renders without Header/NavigationDrawer chrome
// (the gate means the student can't navigate anywhere else anyway).
const DiagnosticPage = lazyWithChunkRetry(
  () => import('./pages/DiagnosticPage')
);
const Homework = lazyWithChunkRetry(() => import('./pages/Homework'));
const AffiliatePortalPage = lazyWithChunkRetry(
  () => import('./pages/AffiliatePortalPage')
);
const HelpRequests = lazyWithChunkRetry(() => import('./pages/HelpRequests'));
const MyRequestsPage = lazyWithChunkRetry(
  () => import('./pages/MyRequestsPage')
);
const UserManagement = lazyWithChunkRetry(
  () => import('./pages/UserManagement')
);
const UsersAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/UsersAdminPage')
);
const UserDetailAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/UserDetailAdminPage')
);
const FeatureFlagAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/FeatureFlagAdminPage')
);
const ProductAnalyticsPage = lazyWithChunkRetry(
  () => import('./pages/admin/ProductAnalyticsPage')
);
const LeadsAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/LeadsAdminPage')
);
const ClassCodesAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/ClassCodesAdminPage')
);
const ClassVisitHistoryPage = lazyWithChunkRetry(
  () => import('./pages/admin/ClassVisitHistoryPage')
);
const AdminClassDetailPage = lazyWithChunkRetry(
  () => import('./pages/admin/AdminClassDetailPage')
);
const LiveClassesPage = lazyWithChunkRetry(
  () => import('./pages/admin/LiveClassesPage')
);
const AttendanceInboxPage = lazyWithChunkRetry(
  () => import('./pages/admin/AttendanceInboxPage')
);
const PaymentsDueAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/PaymentsDueAdminPage')
);
const AccessCodesAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/AccessCodesAdminPage')
);
const NonprofitCohortsAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/NonprofitCohortsAdminPage')
);
const NonprofitOrganizationsAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/NonprofitOrganizationsAdminPage')
);
const NonprofitOrganizationDetailAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/NonprofitOrganizationDetailAdminPage')
);
const AdaptiveTemplateReviewPage = lazyWithChunkRetry(
  () => import('./pages/admin/AdaptiveTemplateReviewPage')
);
const DiagnosticPoolsAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/DiagnosticPoolsAdminPage')
);
const QuestionFeedbackAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/QuestionFeedbackAdminPage')
);
const TeacherQuestionsAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/TeacherQuestionsAdminPage')
);
const PartnersAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/PartnersAdminPage')
);
const ZoomAccountsAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/ZoomAccountsAdminPage')
);
const RevenueAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/RevenueAdminPage')
);
const TeacherStatementsAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/TeacherStatementsAdminPage')
);
const TeacherCalendarFeedsAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/TeacherCalendarFeedsAdminPage')
);
const EnrollmentsAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/EnrollmentsAdminPage')
);
const WorksheetsAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/WorksheetsAdminPage')
);
const HomeworkAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/HomeworkAdminPage')
);
const ZoomActivityAdminPage = lazyWithChunkRetry(
  () => import('./pages/admin/ZoomActivityAdminPage')
);
const ClassStudentsPage = lazyWithChunkRetry(
  () => import('./pages/teacher/students/ClassStudentsPage')
);
const TeacherStudentPage = lazyWithChunkRetry(
  () => import('./pages/teacher/students/StudentPage')
);
const TeacherHomeworkPage = lazyWithChunkRetry(
  () => import('./pages/teacher/TeacherHomeworkPage')
);
const TeacherHomeworkPartReviewView = lazyWithChunkRetry(
  () => import('./pages/teacher/homework/PartReviewView')
);
const HomeworkPrintView = lazyWithChunkRetry(
  () => import('./pages/teacher/homework/HomeworkPrintView')
);
const TeacherQuestionsPage = lazyWithChunkRetry(
  () => import('./pages/teacher/TeacherQuestionsPage')
);
const ProgressPage = lazyWithChunkRetry(() => import('./pages/ProgressPage'));
const HistoryPage = lazyWithChunkRetry(() => import('./pages/HistoryPage'));
const StudentHomeworkReviewPage = lazyWithChunkRetry(
  () => import('./pages/StudentHomeworkReviewPage')
);
const SessionReviewPage = lazyWithChunkRetry(
  () => import('./pages/SessionReviewPage')
);
const TeacherClassCodesPage = lazyWithChunkRetry(
  () => import('./pages/teacher/TeacherClassCodesPage')
);
const PendingApprovalPage = lazyWithChunkRetry(
  () => import('./pages/PendingApprovalPage')
);
const SignupPage = lazyWithChunkRetry(() => import('./pages/SignupPage'));
const NotFoundPage = lazyWithChunkRetry(() => import('./pages/NotFoundPage'));
const {
  LandingPage,
  AboutUsPage,
  ClassJoinPage,
  EnrollPage,
  SubsequentPaymentPage,
  TermsOfUsePage,
  PrivacyPolicyPage,
  BlogListingPage,
  BlogArticlePage,
  PrimarySchoolHubPage,
  SecondarySchoolHubPage,
  Primary1Page,
  Primary2Page,
  Primary3Page,
  Primary4Page,
  Primary5Page,
  Primary6Page,
  Sec1Page,
  Sec2Page,
  Sec3Page,
  Sec4Page,
  SecondaryStreamPage,
  OLevelPage,
  EMathPage,
  AMathPage,
  PSLEPage,
  NLevelPage,
  IPPage,
  IBPage,
  ContactPage,
  SchoolMathPage,
  OnlineMathTuitionPillar,
  OnlinePsleMathTuition,
  OnlineOLevelMathTuition,
  OnlineSecondaryMathTuition,
  OnlineOrInPersonMathTuition,
  OnlineMathTuitionOptions,
  AreaMathTuitionPage,
  PartnershipWelcomePage,
  SubjectPage,
  PsleMathPracticePage,
  SecondaryMathSyllabusPage,
  PrimaryMathSyllabusPage,
} = marketingLazyPages;

const STATIC_LANDING_ROUTES = new Set([
  ...SUBJECT_PAGES.map(page => page.path),
  '/',
  '/about-us',
  // Standalone copied-link walkthrough. It intentionally renders without the
  // shared navigation/footer and is not in the sitemap/prerender registry.
  '/signup',
  // Partnership welcome pages start the same guided signup CTA `/signup` does,
  // over the same three-step card. They differ in everything around it: these
  // carry the shared landing chrome (with the trial CTAs switched off) and are
  // in the sitemap and prerender registry.
  ...PARTNERSHIP_ROUTE_PATHS,
  '/class',
  '/register',
  '/pay',
  '/contact',
  '/blog',
  '/primary-school',
  '/secondary-school',
  // Primary hub pages
  '/primary-1',
  '/primary-2',
  '/primary-3',
  '/primary-4',
  '/primary-5',
  '/primary-6',
  // Secondary hub pages
  '/sec-1',
  '/sec-2',
  '/sec-3',
  '/sec-4',
  // Exam prep pages
  '/o-level',
  '/n-level',
  '/e-math',
  '/a-math',
  '/integrated-programme',
  '/ib',
  // Exam pages
  '/psle',
  // Online tuition cluster
  '/online-math-tuition',
  '/online-psle-math-tuition',
  '/online-o-level-math-tuition',
  '/online-secondary-math-tuition',
  '/online-or-in-person-math-tuition-singapore',
  '/online-math-tuition-options-singapore',
  // Free PSLE practice front door + syllabus hubs
  '/psle-math-practice',
  '/secondary-math-syllabus',
  '/primary-math-syllabus',
]);

const SECONDARY_STREAM_ROUTE_PATTERN = /^\/sec-[1-4]-(g1|g2|g3|ip)$/;
const BLOG_ARTICLE_ROUTE_PATTERN = /^\/blog\/[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SCHOOL_ROUTE_PATTERN = /^\/schools\/[a-z0-9]+(?:-[a-z0-9]+)*$/;
const AREA_ROUTE_PATTERN = /^\/math-tuition\/[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Prefixes that render inside the authenticated app shell (Header + NavigationDrawer). */
const APP_SHELL_ROUTES = new Set([
  '/lesson-prep',
  '/syllabus',
  '/tasks',
  '/practice',
  '/homework',
  '/progress',
  '/history',
  '/help-requests',
  '/my-requests',
  '/affiliate',
  '/user-management',
  '/admin',
  '/callback',
  '/pending-approval',
  '/login',
  '/terms',
  '/privacy',
]);

const APP_SHELL_PREFIXES = [
  '/admin/',
  '/teacher/',
  '/practice/',
  '/homework/',
  '/progress/',
  '/history/',
];

// Routes that opt OUT of the app shell even though their prefix matches above.
// The worksheet print view must render bare so window.print() doesn't capture
// the navigation drawer + header. The homework Save as PDF preview
// (`/teacher/homework/:id/print`) deliberately does not match: it keeps the
// shell on screen and hides it with its own page-scoped print CSS.
const PRINT_VIEW_PATTERN = /^\/teacher\/worksheets\/[^/]+\/print$/;

// Both predicates match a raw pathname against exact sets, so they normalize
// the trailing slash first: React Router serves `/tasks/` from the `/tasks`
// route, and an un-normalized lookup would miss every set here and render an
// authenticated page bare, with no navigation. `hasRouteAccess` normalizes the
// same way so the shell and the role gate can never disagree.
const isAppShellRoute = (rawPathname: string) => {
  const pathname = normalizeRoutePath(rawPathname);
  return (
    !PRINT_VIEW_PATTERN.test(pathname) &&
    (APP_SHELL_ROUTES.has(pathname) ||
      APP_SHELL_PREFIXES.some(prefix => pathname.startsWith(prefix)))
  );
};

const isPublicLandingRoute = (rawPathname: string) => {
  const pathname = normalizeRoutePath(rawPathname);
  return (
    pathname.startsWith('/demo') ||
    STATIC_LANDING_ROUTES.has(pathname) ||
    SECONDARY_STREAM_ROUTE_PATTERN.test(pathname) ||
    BLOG_ARTICLE_ROUTE_PATTERN.test(pathname) ||
    SCHOOL_ROUTE_PATTERN.test(pathname) ||
    AREA_ROUTE_PATTERN.test(pathname) ||
    !isAppShellRoute(pathname)
  );
};

/**
 * When a logged-in user opens /class from in-app nav (via ?inApp=1), show the app shell.
 * Direct visits or unauthenticated users keep the landing layout.
 */
const isClassInAppLayout = (
  pathname: string,
  search: string,
  userRole: string | null | undefined,
  isRoleLoaded: boolean
) =>
  pathname === '/class' &&
  isRoleLoaded &&
  !!userRole &&
  new URLSearchParams(search).get('inApp') === '1';

function App() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const isServerDown = useServerStatus();
  const { user, userRole, isRoleLoaded } = useAuth();
  const location = useLocation();
  const routeLoadingCycleRef = React.useRef({
    locationKey: location.key,
    startedAt: Date.now(),
  });
  if (routeLoadingCycleRef.current.locationKey !== location.key) {
    routeLoadingCycleRef.current = {
      locationKey: location.key,
      startedAt: Date.now(),
    };
  }
  const routeLoadingCycleKey = `route:${location.key}`;
  const routeLoadingResetKey = `${routeLoadingCycleKey}:${user?.sub ?? 'anonymous'}`;
  // Set by the teacher builder pages (via BuilderCanvasActiveContext) while
  // their crafting view is on screen — list vs builder is page state, not a
  // route, so the shell can't derive it from the pathname.
  const [builderCanvasActive, setBuilderCanvasActive] = React.useState(false);
  const { isRail, navWidth, toggleNavRail } = useNavRail(builderCanvasActive);
  // Builder canvases trade the shell's outer gutters (and the lg content cap)
  // for working width — their docked side panels need every horizontal px.
  const isBuilderCanvas = isBuilderCanvasRoute(location.pathname);
  const useAppLayoutForClass = isClassInAppLayout(
    location.pathname,
    location.search,
    userRole,
    isRoleLoaded
  );
  const isLandingRoute = useAppLayoutForClass
    ? false
    : isPublicLandingRoute(location.pathname);

  useDevRoutePersistence();

  // Initialize attribution tracking on app load and record lightweight journey state.
  useAttributionTracking({ enabled: true, pathname: location.pathname });

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  if (isServerDown) {
    return <MaintenancePage />;
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const appRoutes = (
    <ErrorBoundary sourceLocation="app_routes">
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about-us" element={<AboutUsPage />} />
        {/* Partnership welcome pages. Enumerated from the slug table, never
            `/:slug` at the root — a root-level wildcard would swallow every
            unmatched URL in the app. See shared/partnerships/partnershipRoutes.ts. */}
        {PARTNERSHIP_SLUGS.map(slug => (
          <Route
            key={slug}
            path={partnershipRoutePath(slug)}
            element={<PartnershipWelcomePage slug={slug} />}
          />
        ))}
        <Route path="/login" element={<LoginRedirector />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/demo/practice" element={<DemoPractice />} />
        <Route path="/demo/analytics" element={<DemoAnalytics />} />
        <Route path="/callback" element={<AuthCallback />} />
        <Route path="/pending-approval" element={<PendingApprovalPage />} />
        <Route
          path="/lesson-prep"
          element={
            <EnhancedProtectedRoute>
              <TeacherWorksheetsPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/teacher/worksheets/:id/print"
          element={
            <EnhancedProtectedRoute>
              <WorksheetPrintView />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/syllabus"
          element={
            <EnhancedProtectedRoute>
              <CurriculumReview />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/practice/*"
          element={
            <EnhancedProtectedRoute>
              <AdaptivePracticePage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/tasks"
          element={
            <EnhancedProtectedRoute>
              <AdaptivePracticePage variant="tasks" />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/diagnostic"
          element={
            <EnhancedProtectedRoute>
              <DiagnosticPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/help-requests"
          element={
            <EnhancedProtectedRoute>
              <HelpRequests />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/affiliate"
          element={
            <EnhancedProtectedRoute>
              <AffiliatePortalPage />
            </EnhancedProtectedRoute>
          }
        />
        {/* Public legal pages */}
        <Route path="/terms" element={<TermsOfUsePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        {/* Public class join page */}
        <Route path="/class" element={<ClassJoinPage />} />
        {/* Public registration page */}
        <Route path="/register" element={<EnrollPage />} />
        {/* Public contact page */}
        <Route path="/contact" element={<ContactPage />} />
        {/* Public subsequent payment page (magic link only) */}
        <Route path="/pay" element={<SubsequentPaymentPage />} />
        {/* Public blog pages */}
        <Route path="/blog" element={<BlogListingPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        {/* Public level/subject information pages - Hub pages */}
        <Route path="/primary-school" element={<PrimarySchoolHubPage />} />
        <Route path="/secondary-school" element={<SecondarySchoolHubPage />} />
        <Route path="/primary-1" element={<Primary1Page />} />
        <Route path="/primary-2" element={<Primary2Page />} />
        <Route path="/primary-3" element={<Primary3Page />} />
        <Route path="/primary-4" element={<Primary4Page />} />
        <Route path="/primary-5" element={<Primary5Page />} />
        <Route path="/primary-6" element={<Primary6Page />} />
        <Route path="/sec-1" element={<Sec1Page />} />
        <Route path="/sec-2" element={<Sec2Page />} />
        <Route path="/sec-3" element={<Sec3Page />} />
        <Route path="/sec-4" element={<Sec4Page />} />
        {marketingSecondaryStreamRoutes.map(({ path, secLevel, stream }) => (
          <Route
            key={path}
            path={path}
            element={
              <SecondaryStreamPage secLevel={secLevel} stream={stream} />
            }
          />
        ))}
        <Route path="/o-level" element={<OLevelPage />} />
        <Route path="/e-math" element={<EMathPage />} />
        <Route path="/a-math" element={<AMathPage />} />
        {/* Exam-level public pages */}
        <Route path="/psle" element={<PSLEPage />} />
        <Route path="/n-level" element={<NLevelPage />} />
        <Route path="/integrated-programme" element={<IPPage />} />
        <Route path="/ib" element={<IBPage />} />
        {/* Online tuition cluster */}
        <Route
          path="/online-math-tuition"
          element={<OnlineMathTuitionPillar />}
        />
        <Route
          path="/online-psle-math-tuition"
          element={<OnlinePsleMathTuition />}
        />
        <Route
          path="/online-o-level-math-tuition"
          element={<OnlineOLevelMathTuition />}
        />
        <Route
          path="/online-secondary-math-tuition"
          element={<OnlineSecondaryMathTuition />}
        />
        <Route
          path="/online-or-in-person-math-tuition-singapore"
          element={<OnlineOrInPersonMathTuition />}
        />
        <Route
          path="/online-math-tuition-options-singapore"
          element={<OnlineMathTuitionOptions />}
        />
        {SUBJECT_PAGES.map(page => (
          <Route
            key={page.path}
            path={page.path}
            element={<SubjectPage pagePath={page.path} />}
          />
        ))}
        <Route path="/schools/:slug" element={<SchoolMathPage />} />
        {/* Area (neighbourhood) tuition pages — one templated route for all 15
            catchments, mirroring /schools/:slug. Unknown slugs render a
            not-found from within the page, never an empty shell. */}
        <Route path="/math-tuition/:area" element={<AreaMathTuitionPage />} />
        {/* Indexable front door for the adaptive practice demo */}
        <Route path="/psle-math-practice" element={<PsleMathPracticePage />} />
        {/* Syllabus hubs */}
        <Route
          path="/secondary-math-syllabus"
          element={<SecondaryMathSyllabusPage />}
        />
        <Route
          path="/primary-math-syllabus"
          element={<PrimaryMathSyllabusPage />}
        />
        <Route
          path="/my-requests"
          element={
            <EnhancedProtectedRoute>
              <MyRequestsPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/user-management"
          element={
            <EnhancedProtectedRoute>
              <UserManagement />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <EnhancedProtectedRoute>
              <UsersAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/users/:userId"
          element={
            <EnhancedProtectedRoute accessRoute="/admin/users/:userId">
              <UserDetailAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/feature-flags"
          element={
            <EnhancedProtectedRoute>
              <FeatureFlagAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/product-analytics"
          element={
            <EnhancedProtectedRoute accessRoute="/admin/product-analytics">
              <ProductAnalyticsPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/leads"
          element={
            <EnhancedProtectedRoute>
              <LeadsAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/class-codes"
          element={
            <EnhancedProtectedRoute>
              <ClassCodesAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/class-codes/history"
          element={
            <EnhancedProtectedRoute>
              <ClassVisitHistoryPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/live"
          element={
            <EnhancedProtectedRoute>
              <LiveClassesPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/attendance"
          element={
            <EnhancedProtectedRoute>
              <AttendanceInboxPage />
            </EnhancedProtectedRoute>
          }
        />
        {/* Dynamic path — `hasRouteAccess` matches pathnames verbatim, so this
            page self-gates on the literal `/admin/classes/:classCodeId` key. */}
        <Route
          path="/admin/classes/:classCodeId"
          element={
            <EnhancedProtectedRoute>
              <AdminClassDetailPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/billing"
          element={
            <EnhancedProtectedRoute>
              <PaymentsDueAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/access-codes"
          element={
            <EnhancedProtectedRoute>
              <AccessCodesAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/nonprofit-organizations"
          element={
            <EnhancedProtectedRoute>
              <NonprofitOrganizationsAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/nonprofit-organizations/:organizationId"
          element={
            <EnhancedProtectedRoute accessRoute="/admin/nonprofit-organizations/:organizationId">
              <NonprofitOrganizationDetailAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/nonprofit-cohorts"
          element={
            <EnhancedProtectedRoute>
              <NonprofitCohortsAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/adaptive-templates"
          element={
            <EnhancedProtectedRoute>
              <AdaptiveTemplateReviewPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/diagnostic-pools"
          element={
            <EnhancedProtectedRoute>
              <DiagnosticPoolsAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/question-feedback"
          element={
            <EnhancedProtectedRoute>
              <QuestionFeedbackAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/teacher-questions"
          element={
            <EnhancedProtectedRoute>
              <TeacherQuestionsAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/partners"
          element={
            <EnhancedProtectedRoute>
              <PartnersAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/zoom-accounts"
          element={
            <EnhancedProtectedRoute>
              <ZoomAccountsAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/teacher-statements"
          element={
            <EnhancedProtectedRoute>
              <TeacherStatementsAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/revenue"
          element={
            <EnhancedProtectedRoute>
              <RevenueAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/teacher-calendar-feeds"
          element={
            <EnhancedProtectedRoute>
              <TeacherCalendarFeedsAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/enrollments"
          element={
            <EnhancedProtectedRoute>
              <EnrollmentsAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/worksheets"
          element={
            <EnhancedProtectedRoute>
              <WorksheetsAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/homework"
          element={
            <EnhancedProtectedRoute>
              <HomeworkAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/zoom-activity"
          element={
            <EnhancedProtectedRoute>
              <ZoomActivityAdminPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/admin/homework/:id/review"
          element={
            <EnhancedProtectedRoute>
              <TeacherHomeworkPartReviewView mode="admin" />
            </EnhancedProtectedRoute>
          }
        />
        {/* The Students route family (teacherStudents:read). The student page
            and the submission review are dynamic routes, so each gates on its
            literal policy key. */}
        <Route
          path="/teacher/students"
          element={
            <EnhancedProtectedRoute>
              <ClassStudentsPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/teacher/students/:studentId"
          element={
            <EnhancedProtectedRoute accessRoute="/teacher/students/:studentId">
              <TeacherStudentPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/teacher/students/:studentId/homework/:homeworkId"
          element={
            <EnhancedProtectedRoute accessRoute="/teacher/students/:studentId/homework/:homeworkId">
              <StudentHomeworkReviewPage source="teacher" />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/teacher/class-codes"
          element={
            <EnhancedProtectedRoute>
              <TeacherClassCodesPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/teacher/homework"
          element={
            <EnhancedProtectedRoute>
              <TeacherHomeworkPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/teacher/homework/:id/review"
          element={
            <EnhancedProtectedRoute>
              <TeacherHomeworkPartReviewView />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/teacher/homework/:id/print"
          element={
            <EnhancedProtectedRoute accessRoute="/teacher/homework/:id/print">
              <HomeworkPrintView />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/teacher/questions"
          element={
            <EnhancedProtectedRoute>
              <TeacherQuestionsPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/homework/*"
          element={
            <EnhancedProtectedRoute>
              <Homework />
            </EnhancedProtectedRoute>
          }
        />
        {/* Progress chooses its view before mounting practice focus providers. */}
        <Route
          path="/progress"
          element={
            <EnhancedProtectedRoute>
              <ProgressPage />
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <EnhancedProtectedRoute>
              <MathSubjectFocusProvider>
                <HistoryPage />
              </MathSubjectFocusProvider>
            </EnhancedProtectedRoute>
          }
        />
        <Route
          path="/history/homework/:homeworkId"
          element={
            <EnhancedProtectedRoute accessRoute="/history/homework/:homeworkId">
              <StudentHomeworkReviewPage />
            </EnhancedProtectedRoute>
          }
        />
        {/* Deliberately un-rowed in shared/accessPolicy.ts (allow-on-miss
            detail-route idiom): teachers have no /history access but open a
            student's session from the Students student page. The page
            self-gates and the server 403s a cross-actor read. */}
        <Route
          path="/history/session/:sessionId"
          element={
            <EnhancedProtectedRoute>
              <SessionReviewPage />
            </EnhancedProtectedRoute>
          }
        />
        {/* Renamed-route bridges: /mastery → /progress and its session review
            → /history/session/:id. Query (?view, ?student) is preserved. */}
        <Route
          path="/mastery"
          element={<LegacyRouteRedirect build={() => '/progress'} />}
        />
        {/* Class Progress retired into Students: a nonprofit cohort's
            heatmap and triage now sit on its class page. The bridge keeps
            old bookmarks landing on the class they named (?class=). */}
        <Route
          path="/teacher/class-progress"
          element={<LegacyRouteRedirect build={() => '/teacher/students'} />}
        />
        <Route
          path="/mastery/session/:sessionId"
          element={
            <LegacyRouteRedirect
              build={params =>
                `/history/session/${encodeURIComponent(params.sessionId ?? '')}`
              }
            />
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ErrorBoundary>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <Helmet>
        <title>{getBrandingConfig()?.siteTitle || 'DeepThink'}</title>
        <meta
          name="description"
          content={
            getBrandingConfig()?.metaDescription ||
            'DeepThink - Educational Question Management System'
          }
        />
      </Helmet>
      <ScrollToTop />
      {!isLandingRoute && (
        <>
          <Header navRailActive={isRail} />
          <Box
            component="nav"
            sx={{
              width: { sm: navWidth },
              flexShrink: { sm: 0 },
              ...navWidthTransitionSx('width'),
            }}
          >
            {isRoleLoaded && (
              <NavigationDrawer
                mobileOpen={mobileOpen}
                handleDrawerToggle={handleDrawerToggle}
                userRole={userRole}
                railActive={isRail}
                onToggleRail={toggleNavRail}
              />
            )}
          </Box>
        </>
      )}

      <Box
        component="main"
        className={isLandingRoute ? '' : 'safe-mobile-all-with-bottom-nav'}
        sx={{
          flexGrow: 1,
          // Flex items default to min-width:auto, which lets wide intrinsic
          // content (data tables, wide toolbars) grow this column past the
          // viewport and scroll the whole page sideways on mobile. Pin to 0 so
          // `overflow: clip` (below) and inner TableContainers actually engage.
          minWidth: 0,
          p:
            isLandingRoute || useAppLayoutForClass
              ? 0
              : { xs: 0, sm: 2, md: 3 },
          // Mirror every breakpoint of `p` — sx merges responsive values into
          // per-breakpoint buckets, so a missing md key here would lose to
          // p's md padding.
          ...(isBuilderCanvas &&
            !isLandingRoute &&
            !useAppLayoutForClass && { px: { xs: 0, sm: 1.5, md: 1.5 } }),
          width: isLandingRoute ? '100%' : { sm: `calc(100% - ${navWidth}px)` },
          ...(!isLandingRoute && navWidthTransitionSx('width')),
          minHeight: '100vh',
          // `clip` (not `hidden`): identical clipping, but it doesn't create
          // a scroll container, so the builders' side rails (outcome list,
          // docked items tray) can position: sticky against the page scroll.
          overflow: 'clip',
          // Desktop padding overrides mobile CSS class
          pt: isLandingRoute || useAppLayoutForClass ? 0 : { sm: 2, md: 3 },
          pb: isLandingRoute || useAppLayoutForClass ? 0 : { sm: 2, md: 3 },
        }}
      >
        {/* Desktop toolbar spacer (omit on landing) */}
        {!isLandingRoute && (
          <Toolbar sx={{ display: { xs: 'none', sm: 'block' } }} />
        )}
        <Container
          maxWidth={
            isLandingRoute || useAppLayoutForClass
              ? false
              : isBuilderCanvas
                ? 'xl'
                : 'lg'
          }
          disableGutters={
            isLandingRoute || useAppLayoutForClass || isBuilderCanvas
          }
          sx={{
            // Builder zero mirrors the theme's MuiContainer gutter breakpoints
            // (base + sm) — the theme override wins over a plain `px: 0`.
            px:
              isLandingRoute || useAppLayoutForClass
                ? 0
                : isBuilderCanvas
                  ? { xs: 0, sm: 0 }
                  : { xs: 0, sm: 2, md: 3 },
            width: '100%',
            // `clip`, not `hidden` — keeps position: sticky working (see the
            // main Box above).
            overflow: 'clip',
          }}
        >
          <BuilderCanvasActiveContext.Provider value={setBuilderCanvasActive}>
            <StudentLoadingCycleProvider>
              <StudentLoadingCycleScope
                cycleKey={routeLoadingCycleKey}
                startedAt={routeLoadingCycleRef.current.startedAt}
                resetKey={routeLoadingResetKey}
              >
                <FocusTransitionProvider>
                  {/* Keep this boundary stable from the first client mount.
                      Inserting it after boot remounts the page and loses the
                      menu/dialog state opened by a replayed first click. */}
                  <Suspense fallback={<AppRouteLoadingFallback />}>
                    {appRoutes}
                  </Suspense>
                </FocusTransitionProvider>
              </StudentLoadingCycleScope>
            </StudentLoadingCycleProvider>
          </BuilderCanvasActiveContext.Provider>
        </Container>
      </Box>
    </Box>
  );
}

export default App;
